import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

// We are using the same regex that the Android app is using
const emailRegex =
  /^[a-zA-Z0-9+._%-+]{1,256}@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,25})+$/;

const EmailInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    hint,
    hidden, 
    validate_format: validateFormat,
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );
    
  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [{ isActive, isRequired, isReadOnly, isElementValid, initialAnswer }] =
    useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: (value) => validateEmailFormatAndValue(value, validateFormat, isElementValid, validateMsg),
      required: isRequired ? 'This field is required' : false,
    });
    
  }, [isElementValid, isRequired, currentValue]);

  useEffect(() => {
    changeValue(initialAnswer);
  }, [initialAnswer]);

  const onChange = (value) => {
    changeValue(value, true);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  const validateEmailFormatAndValue = (email, validateFormat, isElementValid, validateMsg) => {
    if (!email) return;
  
    if (validateFormat && !email.match(emailRegex)) {
      return 'Invalid email format';
    }
  
    if (!isElementValid) {
      return validateMsg;
    }
  };

  if (!isActive || hidden|| !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <input
        name={identifier}
        id={identifier}
        // value={currentValue || ''}
        defaultValue={currentValue || ''}
        type='text'
        onClick={() => dispatch(setCurrentSelectedMultimediaInput({}))}
        className={clsx(
          errorMessage !== undefined && 'invalid',
          hasResourceUpdateHighlight && 'updateHighlight',
          isReadOnly && 'read-only'
        )}
        onChange={(e) => onChange(e.target.value.trim())}
        onFocus={removeResourceUpdateHighlight}
        readOnly={isReadOnly}
      />
      {hint && <p className='element-hint'>{hint}</p>}
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

EmailInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    initialAnswer: PropTypes.string,
    validate_format: PropTypes.bool,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(EmailInput);
